import { useEffect, useState } from 'react';
import { BILL_DOCUMENT_SETTING, resolveBillDocument } from '@/printing/billDocument';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ShoppingCart, Package, Receipt, BarChart3, Settings, LogOut, Truck, Users, WalletCards, Percent, Building2, RefreshCw, Activity, Maximize2, Minimize2, ChevronLeft, Menu, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/store/useAuthStore';
import { useAppStore } from '@/store/useAppStore';
import { hydrateSettingsFromDatabase, installSettingsPersister } from '@/store/hydrateSettingsFromDb';
import { useSettingsStore } from '@/store/useSettingsStore';
import { appInfo } from '@/platform';
import { useSync } from '@/sync/useSync';
import { ensureDeviceSlot } from '@/services/DeviceSlotService';
import { PlanStatusBanner, usePlanGate, FEATURE_PURCHASING } from '@/plans';
import { UpdateBanner } from '@/components/UpdateBanner';
import { BackendOfflineBanner } from '@/components/BackendOfflineBanner';
import { SyncHealthBanner } from '@/components/SyncHealthBanner';
import { KeyboardHelpOverlay } from '@/components/KeyboardHelpOverlay';
import { BrandMark } from '@/components/BrandMark';
import {
  usePermissions,
  DASHBOARD_VIEW,
  BILLING_CREATE,
  BILLING_VIEW,
  INVENTORY_VIEW,
  CUSTOMER_VIEW,
  PURCHASING_VIEW,
  REPORTS_VIEW,
  SETTINGS_MANAGE,
  ACTIVITY_LOG_VIEW,
  type Permission,
} from '@/rbac';
import { FOCUS_ROUTES, readFocusPref, writeFocusPref } from '@/hooks/useFocusMode';
import { StoreSwitcher } from './StoreSwitcher';
// Side-effect import: instantiates WatermelonDB once for the renderer.
import '@/db';
import { useSyncHealth, describeAge } from '@/sync/useSyncHealth';

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Required permission — hidden when the role lacks it. */
  permission?: Permission;
  /** When true, the item is rendered but disabled. */
  disabled?: boolean;
};

type NavSection = {
  /** Section header; omit for the trailing ungrouped block (e.g. Settings). */
  heading?: string;
  items: readonly NavItem[];
};

const NAV_SECTIONS: readonly NavSection[] = [
  {
    heading: 'Operations',
    items: [
      { to: '/',          label: 'Dashboard', icon: LayoutDashboard, permission: DASHBOARD_VIEW },
      { to: '/billing',   label: 'Billing',   icon: ShoppingCart,    permission: BILLING_CREATE },
      { to: '/invoices',  label: 'Invoices',  icon: Receipt,         permission: BILLING_VIEW },
      { to: '/customers', label: 'Customers', icon: Users,           permission: CUSTOMER_VIEW },
    ],
  },
  {
    heading: 'Catalog',
    items: [
      { to: '/inventory',       label: 'Inventory',  icon: Package,      permission: INVENTORY_VIEW },
      { to: '/suppliers',       label: 'Suppliers',  icon: Building2,    permission: PURCHASING_VIEW },
      { to: '/purchase-orders', label: 'PO List',    icon: Truck,        permission: PURCHASING_VIEW },
      { to: '/purchases/new',   label: 'Receive',    icon: ShoppingCart, permission: PURCHASING_VIEW },
      { to: '/promotions',      label: 'Promotions', icon: Percent,      permission: SETTINGS_MANAGE },
    ],
  },
  {
    heading: 'Insights',
    items: [
      { to: '/expenses', label: 'Expenses', icon: WalletCards, permission: REPORTS_VIEW },
      { to: '/reports',  label: 'Reports',  icon: BarChart3,   permission: REPORTS_VIEW },
      { to: '/activity', label: 'Activity Log', icon: Activity, permission: ACTIVITY_LOG_VIEW },
    ],
  },
  {
    items: [
      { to: '/settings', label: 'Settings', icon: Settings, permission: SETTINGS_MANAGE },
    ],
  },
];

export function AppShell() {
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();
  const location = useLocation();
  const [version, setVersion] = useState<string>('');
  const [focusPref, setFocusPref] = useState(readFocusPref);
  // Phones and tablets: the rail is a drawer. Closed on every navigation, so
  // tapping a link lands on the page rather than leaving the menu over it.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);
  const { isSyncing, lastSyncAt, syncError, triggerSync } = useSync();
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  // Whether the shop's data is actually safe, as distinct from whether the last
  // sync happened to succeed. `lastSyncAt` doubles as the tick that refreshes
  // it, so the counts move the moment a cycle finishes.
  const health = useSyncHealth(activeStoreId, lastSyncAt);

  const printerSettings = useSettingsStore((s) => s.settings);
  const { can } = usePermissions();
  const { canAccess } = usePlanGate();

  // Filter NAV by RBAC + plan-feature availability, dropping empty sections.
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.permission && !can(item.permission)) return false;
      if (item.to === '/purchases/new' && !canAccess(FEATURE_PURCHASING)) return false;
      return true;
    }),
  })).filter((section) => section.items.length > 0);

  useEffect(() => {
    appInfo.getVersion().then(setVersion).catch(() => undefined);
  }, []);

  // Trigger an initial sync once we have an active store, then make a
  // best-effort attempt to pre-allocate the device slot so the first
  // sale doesn't fall back to a hash-based slot.
  useEffect(() => {
    if (!activeStoreId) return;
    // Settings live in the `settings` table but the cache was only ever
    // restored from the local key-value store, which sign-out clears — so a
    // fresh session silently ran on defaults: standard invoice layout, no bank
    // block, Receipt Designer choices gone. Read them back before the first
    // sync, since the print pipeline reads this cache synchronously.
    void hydrateSettingsFromDatabase(activeStoreId);
    // Make every setSetting on the settings screens durable, not cache-only.
    void installSettingsPersister(activeStoreId);
    triggerSync();
    void ensureDeviceSlot();
    // triggerSync is intentionally omitted: its identity changes whenever
    // auth/store state updates, which would re-fire this effect and hammer
    // the rate-limited /sync/pull endpoint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStoreId]);

  // Shift+F toggles the frame. Shift-modified so it cannot collide with the
  // product search, which is always focused on the billing screen and must
  // keep receiving plain letters and barcode-scanner input.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'F' || !e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (!FOCUS_ROUTES.has(window.location.pathname)) return;
      e.preventDefault();
      setFocusPref((prev) => {
        writeFocusPref(!prev);
        return !prev;
      });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const syncLabel = isSyncing
    ? 'syncing…'
    : syncError
      ? 'failed'
      : lastSyncAt
        ? `last ${new Date(lastSyncAt).toLocaleTimeString()}`
        : 'idle';
  // The bill this till prints (browser print dialog). It used to show the
  // network-printer flag, which a browser can never use — so every web till
  // read "Printer: disabled" however it was set up.
  const printerLabel = resolveBillDocument(printerSettings[BILL_DOCUMENT_SETTING]) === 'a4'
    ? 'A4 invoice'
    : `receipt ${printerSettings.THERMAL_PRINTER_PAPER_WIDTH || '80'} mm`;

  // Focus mode stays *inside* AppShell rather than becoming its own top-level
  // route. That is deliberate: the effects above — the first sync and the
  // device-slot pre-allocation — must still run when a till is reloaded
  // straight onto /billing. Without a slot, invoice numbers fall back to a
  // hash-based slot, which is exactly what you do not want on a live counter.
  const onFocusRoute = FOCUS_ROUTES.has(location.pathname);
  const focused = onFocusRoute && focusPref;

  const setFocus = (on: boolean) => {
    setFocusPref(on);
    writeFocusPref(on);
  };

  /** Status banners. In the top bar on wide screens; above the page on phones. */
  const banners = (
    <>
      <PlanStatusBanner />
      <BackendOfflineBanner />
      <SyncHealthBanner health={health} onSyncNow={triggerSync} isSyncing={isSyncing} />
      <UpdateBanner />
    </>
  );

  /** Compact sync control for the focus strip — the status footer is hidden. */
  const syncButton = (
    <button
      onClick={() => !isSyncing && triggerSync()}
      disabled={isSyncing}
      title={syncError ? syncError : `Sync — ${syncLabel}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-label uppercase transition-colors duration-120 ease-apple',
        isSyncing
          ? 'cursor-wait text-fg-tertiary'
          : syncError
            ? 'text-danger-ink hover:bg-white/10'
            : 'text-fg-secondary hover:bg-white/10 hover:text-fg-primary',
      )}
    >
      <RefreshCw size={11} className={cn(isSyncing && 'animate-spin')} />
      {isSyncing ? 'Syncing…' : syncError ? 'Sync failed' : 'Synced'}
    </button>
  );

  const focusToggle = (
    <button
      onClick={() => setFocus(!focused)}
      title={focused ? 'Show the full app (Shift+F)' : 'Full-window billing (Shift+F)'}
      aria-pressed={focused}
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-label uppercase text-fg-secondary transition-colors duration-120 ease-apple hover:bg-white/10 hover:text-fg-primary"
    >
      {focused ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
      <span className="hidden sm:inline">{focused ? 'Exit full' : 'Full window'}</span>
    </button>
  );

  // ── Focus frame ───────────────────────────────────────────────────
  // A 40px strip instead of 56 + 28 of chrome and a 240px rail, and the
  // surface runs nearly edge to edge. Everything else — the boot effects,
  // the "?" cheat-sheet, RBAC context — is unchanged, because this is the
  // same component rendering a different frame.
  if (focused) {
    return (
      <div className="app-backdrop grid h-screen grid-rows-[40px_1fr]">
        <header className="dark chrome glass-chrome flex items-center justify-between border-b border-white/10 px-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <button
              onClick={() => navigate('/')}
              title="Back to the app"
              className="inline-flex items-center gap-1 rounded-md py-1 pl-1 pr-2 text-body text-fg-secondary transition-colors duration-120 ease-apple hover:bg-white/10 hover:text-fg-primary"
            >
              <ChevronLeft size={16} />
              <BrandMark size={18} />
            </button>
            <span className="h-3.5 w-px shrink-0 bg-white/15" aria-hidden />
            <StoreSwitcher />
          </div>
          <div className="hidden min-w-0 flex-1 items-center justify-center gap-3 px-4 md:flex">
            {banners}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {syncButton}
            {focusToggle}
          </div>
        </header>

        {/* Near edge-to-edge: 6px gutter, 12px inner padding. The billing
            surface is `h-full flex-col`, so it fills whatever it is given. */}
        <main className="flex min-h-0 flex-col gap-1.5 overflow-hidden p-1.5">
          <div className="flex flex-col gap-1.5 empty:hidden md:hidden">{banners}</div>
          <div className="content-sheet min-h-0 flex-1 overflow-auto rounded-xl p-2 text-fg-primary sm:p-3">
            <Outlet />
          </div>
        </main>

        <KeyboardHelpOverlay />
      </div>
    );
  }

  return (
    <div className="app-backdrop grid h-screen grid-cols-1 grid-rows-[52px_1fr_28px] lg:grid-cols-[240px_1fr] lg:grid-rows-[56px_1fr_28px]">
      {/* Top bar -----------------------------------------------------
          `dark chrome` re-points the semantic tokens for the subtree, so
          StoreSwitcher and the status banners render light-on-charcoal
          without knowing they are sitting on glass. See theme.css. */}
      <header className="dark chrome glass-chrome flex items-center justify-between gap-2 border-b border-white/10 px-2 sm:px-5 lg:col-span-2">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            aria-expanded={navOpen}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-fg-secondary hover:bg-white/10 hover:text-fg-primary lg:hidden"
          >
            <Menu size={18} />
          </button>
          <BrandMark size={22} />
          <span className="hidden text-body font-medium tracking-tight sm:inline">BigxBook</span>
          <span className="hidden h-4 w-px bg-white/15 sm:block" aria-hidden />
          <StoreSwitcher />
        </div>
        <div className="hidden flex-1 items-center justify-center gap-3 px-6 md:flex">
          {banners}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {/* Only on a focus route — elsewhere there is nothing to expand. */}
          {onFocusRoute && focusToggle}
          <button
            onClick={signOut}
            aria-label="Sign out"
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-body text-fg-secondary hover:bg-white/10 hover:text-fg-primary transition-colors duration-120 ease-apple"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      {/* Phones and tablets: the rail slides in over the page. */}
      {navOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      )}

      {/* Left rail ---------------------------------------------------
          A darker tint and a heavier blur than the top bar, so the two
          panes read as different depths rather than one flat slab. */}
      <aside
        className={cn(
          'dark chrome glass-rail overflow-y-auto border-r border-white/10 p-3',
          // Wide: the static rail in its grid cell. Narrow: an off-canvas drawer.
          'fixed inset-y-0 left-0 z-50 w-72 transition-transform duration-200 ease-apple lg:static lg:row-start-2 lg:w-auto lg:translate-x-0 lg:transition-none',
          navOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
        )}
        aria-label="Main menu"
      >
        <div className="mb-3 flex items-center justify-between lg:hidden">
          <span className="inline-flex items-center gap-2 px-1 text-body font-medium">
            <BrandMark size={20} /> BigxBook
          </span>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close menu"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-fg-secondary hover:bg-white/10"
          >
            <X size={18} />
          </button>
        </div>
        {visibleSections.map((section, idx) => (
          <div key={section.heading ?? `section-${idx}`} className={cn(idx > 0 && 'mt-5')}>
            {section.heading && (
              <p className="px-3 pb-1.5 text-label uppercase tracking-wide text-fg-tertiary">
                {section.heading}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map(({ to, label, icon: Icon, disabled }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-body transition-colors duration-120 ease-apple lg:py-2',
                      disabled && 'pointer-events-none text-fg-tertiary',
                      // Active is a lit pane of its own — a brighter tint plus
                      // the same top hairline the chrome carries.
                      !disabled && isActive &&
                        'bg-white/[0.10] text-fg-primary font-medium shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]',
                      !disabled && !isActive &&
                        'text-fg-secondary hover:bg-white/[0.06] hover:text-fg-primary',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && !disabled && (
                        <span
                          aria-hidden
                          className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-accent shadow-[0_0_10px_rgb(var(--accent)/0.7)]"
                        />
                      )}
                      <Icon
                        size={16}
                        strokeWidth={1.75}
                        className={cn(isActive && !disabled && 'text-accent-ink')}
                      />
                      <span>{label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </aside>

      {/* Content -----------------------------------------------------
          The light sheet floats on the charcoal field with a gutter all
          round, which is what gives the chrome something to be glass
          *against*. The inner div is the scroll container, so screens'
          `sticky` headers behave exactly as before. */}
      <main className="row-start-2 flex min-h-0 flex-col gap-1.5 overflow-hidden p-1.5 sm:p-3 lg:col-start-2">
        <div className="flex flex-col gap-1.5 empty:hidden md:hidden">{banners}</div>
        <div className="content-sheet min-h-0 flex-1 overflow-auto rounded-xl p-3 text-fg-primary sm:rounded-2xl sm:p-5 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Status footer ----------------------------------------------- */}
      <footer className="dark chrome glass-chrome flex items-center justify-between gap-2 border-t border-white/10 px-3 text-label text-fg-tertiary uppercase sm:px-4 lg:col-span-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate">Sync: {syncLabel}</span>
          {/* The part the old bar never said. "Last 4:05 PM" is the time of the
              last SUCCESS and says nothing about what is stuck behind it. */}
          {health.pending > 0 && (
            <span
              title={health.message ?? undefined}
              className={cn(
                'rounded px-1.5 py-0.5 normal-case',
                health.severity === 'alert'
                  ? 'bg-danger/20 text-danger-ink'
                  : health.severity === 'warn'
                    ? 'bg-warning/20 text-warning-ink'
                    : 'text-fg-secondary',
              )}
            >
              {health.pending} waiting
              {health.oldestAt != null && ` · ${describeAge(health.oldestAgeMs)}`}
            </span>
          )}
          <button
            onClick={() => !isSyncing && triggerSync()}
            disabled={isSyncing}
            title={syncError ? syncError : 'Sync now'}
            className={cn(
              'inline-flex items-center gap-1 rounded px-2 py-0.5 text-label uppercase transition-colors duration-120 ease-apple',
              isSyncing
                ? 'text-fg-tertiary cursor-wait'
                : syncError
                  ? 'text-danger-ink hover:bg-white/10'
                  : 'text-fg-secondary hover:bg-white/10 hover:text-fg-primary',
            )}
          >
            <RefreshCw size={11} className={cn(isSyncing && 'animate-spin')} />
            {isSyncing ? 'Syncing…' : 'Sync now'}
          </button>
        </div>
        <span className="hidden md:inline">Prints: {printerLabel}</span>
        <span className="hidden sm:inline">v{version}</span>
      </footer>

      {/* App-wide "?" keyboard cheat-sheet */}
      <KeyboardHelpOverlay />
    </div>
  );
}
